/**
 * Fixed bottom bar. Not a draggable window — everything else in the UI is.
 * Layout (left → right):
 *   [Weapon specs panel + 4 suit-labeled slots] | [stealth status + mode]
 *   | [menu buttons]
 * Slots follow the user mockup: ♣ Main · ♦ Side · ♠ Melee · ♥ Heal.
 */

import { CONFIG } from '../config';
import { BODY_PARTS } from '../sim/body';
import type { GuardState } from '../sim/guard';
import type { InputState } from '../sim/input';
import type { Player } from '../sim/player';
import type { MapData } from '../sim/map';
import { roomAt } from '../sim/light';
import { isRestrictedFor } from '../sim/disguise';

const SUITS = ['♣', '♦', '♠', '♥'] as const;
const SLOT_LABELS = ['MAIN', 'SIDE', 'MELEE', 'HEAL'] as const;

const STEALTH_LABEL: Record<GuardState, string> = {
  unaware: 'STEALTH',
  suspicious: 'SUSPECTED',
  cautious: 'HUNTED',
  alert: 'ENGAGED',
};

const STEALTH_COLOR: Record<GuardState, string> = {
  unaware: '#8a4ac0',
  suspicious: '#d0a040',
  cautious: '#d06040',
  alert: '#ff4040',
};

export class Taskbar {
  private readonly root: HTMLElement;
  private readonly slotEls: HTMLElement[];
  private readonly ammoEls: HTMLElement[];
  private readonly specsEl: HTMLElement;
  private readonly stealthLabel: HTMLElement;
  private readonly stealthFill: HTMLElement;
  private readonly noiseFill: HTMLElement;
  private readonly hpFill: HTMLElement;
  private readonly staFill: HTMLElement;
  private readonly modeValue: HTMLElement;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('taskbar');
    const slotsHtml = SUITS.map((suit, i) => `
      <div class="tb-slot" data-idx="${i}">
        <div class="tb-slot-suit">${suit}</div>
        <div class="tb-slot-label">${SLOT_LABELS[i].split('').join('\n')}</div>
        <div class="tb-slot-ammo">--/--</div>
        <div class="tb-slot-key">${i + 1}</div>
      </div>`).join('');
    this.root.innerHTML = `
      <div class="tb-left">
        <div class="tb-specs">
          <div class="tb-specs-title">WEAPON</div>
          <div class="tb-specs-body">—</div>
        </div>
        <div class="tb-slots">${slotsHtml}</div>
        <div class="tb-vitals">
          <div class="tb-vbar"><span class="tb-vbar-label">HP</span><span class="tb-vbar-track"><span class="tb-hp-fill"></span></span></div>
          <div class="tb-vbar"><span class="tb-vbar-label">STA</span><span class="tb-vbar-track"><span class="tb-sta-fill"></span></span></div>
        </div>
      </div>
      <div class="tb-center">
        <div class="tb-stealth-title">STEALTH STATUS</div>
        <div class="tb-stealth-value"><span class="tb-stealth-text">STEALTH</span></div>
        <div class="tb-stealth-track"><span class="tb-stealth-fill"></span></div>
        <div class="tb-noise-line">
          <span class="tb-noise-label">NOISE</span>
          <span class="tb-noise-track"><span class="tb-noise-fill"></span></span>
        </div>
        <div class="tb-mode-line">
          <span class="tb-mode-label">MODE</span>
          <span class="tb-mode-value">WALK</span>
        </div>
      </div>
      <div class="tb-right">
        <button class="tb-btn">MAP</button>
        <button class="tb-btn tb-btn-inv">INV</button>
        <button class="tb-btn tb-btn-opt">OPT</button>
      </div>
    `;
    this.slotEls = Array.from(this.root.querySelectorAll('.tb-slot')) as HTMLElement[];
    this.ammoEls = this.slotEls.map((s) => s.querySelector('.tb-slot-ammo') as HTMLElement);
    this.specsEl = this.root.querySelector('.tb-specs-body') as HTMLElement;
    this.stealthLabel = this.root.querySelector('.tb-stealth-text') as HTMLElement;
    this.stealthFill = this.root.querySelector('.tb-stealth-fill') as HTMLElement;
    this.noiseFill = this.root.querySelector('.tb-noise-fill') as HTMLElement;
    this.hpFill = this.root.querySelector('.tb-hp-fill') as HTMLElement;
    this.staFill = this.root.querySelector('.tb-sta-fill') as HTMLElement;
    this.modeValue = this.root.querySelector('.tb-mode-value') as HTMLElement;
  }

  /** Wire the INV taskbar button (e.g. to toggle the inventory window). */
  onInventoryClick(cb: () => void): void {
    this.root.querySelector('.tb-btn-inv')?.addEventListener('click', cb);
  }

  /** Wire the OPT taskbar button (opens the Esc / options menu). */
  onOptionsClick(cb: () => void): void {
    this.root.querySelector('.tb-btn-opt')?.addEventListener('click', cb);
  }

  update(
    player: Player,
    _guardCount = 0,
    worstState: GuardState = 'unaware',
    _input?: InputState,
    noise?: { current: number; max: number },
    seenNow = false,
    map?: MapData,
  ): void {
    // Weapon slots
    for (let i = 0; i < this.slotEls.length; i++) {
      const slot = this.slotEls[i]!;
      const w = player.weapons[i];
      slot.classList.toggle('tb-slot--active', i === player.activeWeaponIdx);
      if (!w) continue;
      const ammoEl = this.ammoEls[i]!;
      if (w.kind === 'heal') {
        ammoEl.textContent = `${w.ammo ?? 0}/${w.magazine ?? 0}`;
        ammoEl.style.color = (w.ammo ?? 0) === 0 ? '#e05050' : '#66ff88';
      } else if (w.isMelee) {
        ammoEl.textContent = '∞';
        ammoEl.style.color = '#a0b0c0';
      } else {
        ammoEl.textContent = `${w.ammo}/${w.magazine}`;
        const pct = (w.ammo ?? 0) / (w.magazine ?? 1);
        ammoEl.style.color = pct === 0 ? '#e05050' : pct < 0.25 ? '#d0a040' : '#a0d090';
      }
    }

    // Active weapon specs panel.
    const active = player.weapons[player.activeWeaponIdx];
    if (active) this.specsEl.innerHTML = renderSpecs(active);

    // HP (torso + head averaged) + stamina
    const vitals = (player.body.hp.torso / player.body.hpMax.torso + player.body.hp.head / player.body.hpMax.head) / 2;
    const hpPct = Math.max(0, Math.min(1, vitals));
    this.hpFill.style.width = `${(hpPct * 100).toFixed(0)}%`;
    let crippled = false;
    for (const p of BODY_PARTS) if (player.body.crippled[p]) crippled = true;
    this.hpFill.style.background = player.body.dead ? '#404040' : hpPct < 0.3 ? '#e05050' : crippled ? '#c07030' : '#c04040';

    const staPct = Math.max(0, Math.min(1, player.stamina / CONFIG.stamina.max));
    this.staFill.style.width = `${(staPct * 100).toFixed(0)}%`;
    this.staFill.style.background = player.exhausted ? '#c07030' : '#4ba04b';

    // Stealth status — "SEEN!" (a guard has live eyes on you) overrides the
    // awareness-ladder readout.
    if (seenNow) {
      this.stealthLabel.textContent = 'SEEN!';
      this.stealthLabel.style.color = '#ff2d2d';
      this.stealthFill.style.background = '#ff2d2d';
      this.stealthFill.style.width = '100%';
    } else if (player.disguise) {
      // In uniform: what matters isn't whether they see you, it's whether the
      // room you're standing in makes sense for what you're wearing.
      const room = map ? roomAt(map, player.x, player.y) : null;
      const wrongRoom = isRestrictedFor(player.disguise, room?.kind);
      this.stealthLabel.textContent = wrongRoom ? 'OUT OF PLACE' : 'BLENDING IN';
      this.stealthLabel.style.color = wrongRoom ? '#e0b040' : '#8ad0ff';
      this.stealthFill.style.background = wrongRoom ? '#e0b040' : '#8ad0ff';
      this.stealthFill.style.width = wrongRoom ? '55%' : '20%';
    } else if (player.concealedAt) {
      // Shut inside a prop — unseeable, so the ladder readout would be a lie.
      this.stealthLabel.textContent = 'CONCEALED';
      this.stealthLabel.style.color = '#22e6ff';
      this.stealthFill.style.background = '#22e6ff';
      this.stealthFill.style.width = '8%';
    } else {
      this.stealthLabel.textContent = STEALTH_LABEL[worstState];
      this.stealthLabel.style.color = STEALTH_COLOR[worstState];
      this.stealthFill.style.background = STEALTH_COLOR[worstState];
      const stealthPct = { unaware: 0.15, suspicious: 0.4, cautious: 0.7, alert: 1 }[worstState];
      this.stealthFill.style.width = `${stealthPct * 100}%`;
    }

    // Noise: how loud the player is right now vs the loudest they could be.
    if (noise) {
      const noisePct = noise.max > 0 ? Math.max(0, Math.min(1, noise.current / noise.max)) : 0;
      this.noiseFill.style.width = `${(noisePct * 100).toFixed(0)}%`;
      this.noiseFill.style.background = noisePct > 0.66 ? '#ff5b4a' : noisePct > 0.33 ? '#e0b040' : '#4bb0a0';
    }

    // Mode line: current stance (crouch state now lives in the stance display).
    this.modeValue.textContent = player.stance.toUpperCase();
    this.modeValue.style.color = player.stance === 'run'
      ? '#e0a040'
      : player.stance === 'crouch'
      ? '#60c0a0'
      : '#c0d0e0';
  }
}

function renderSpecs(w: import('../sim/weapons').Weapon): string {
  const line = (k: string, v: string): string =>
    `<div class="tb-specs-row"><span class="tb-specs-k">${k}</span><span class="tb-specs-v">${v}</span></div>`;
  const rows: string[] = [];
  rows.push(line('NAME', w.name));
  if (w.kind === 'heal') {
    rows.push(line('CHARGE', `${w.ammo}/${w.magazine}`));
    rows.push(line('HEAL', '+30 / limb'));
    rows.push(line('COOL', `${w.fireIntervalSec.toFixed(2)}s`));
  } else if (w.isMelee) {
    rows.push(line('AMMO', '∞'));
    rows.push(line('DMG', `${w.damage}`));
    rows.push(line('REACH', 'adj'));
  } else {
    rows.push(line('AMMO', `${w.ammo}${w.magazine !== undefined ? `/${w.magazine}` : ''}${w.unlimitedAmmo ? ' ∞' : ''}`));
    rows.push(line('DMG', `${w.damage}`));
    rows.push(line('RANGE', `${w.rangeTiles}t`));
    rows.push(line('CYCLE', `${w.fireIntervalSec.toFixed(2)}s`));
    rows.push(line('SOUND', `${w.loudness}`));
  }
  if (w.cooldown > 0.05) rows.push(line('CD', `${w.cooldown.toFixed(2)}s`));
  return rows.join('');
}
